import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';
import {
  Box,
  Collapse,
  IconButton,
  SxProps,
  TableCell,
  TableRow,
  Theme,
} from '@mui/material';
import React, { ReactNode } from 'react';

interface ExpandableRowProps {
  // Current open state
  open: boolean;
  // Handler to toggle the open state
  onToggle: () => void;
  // Row ID (used as a key for the component if needed)
  id?: string | number;
  // Row content (array of TableCells)
  cells: ReactNode[];
  // Content to display when expanded
  expandedContent: ReactNode;
  // Number of columns (for colSpan when expanded)
  colSpan: number;
  // Row styles
  rowSx?: SxProps<Theme>;
  // Width of the icon cell
  iconCellWidth?: string | number;
  // Custom handler for row click (defaults to expand/collapse)
  onRowClick?: () => void;
  // Additional cell for custom actions
  actionCell?: ReactNode;
}

/**
 * Generic expandable table row component
 * Clickable row that displays additional information when expanded
 */
export const ExpandableRow: React.FC<ExpandableRowProps> = (props: ExpandableRowProps) => {
  const {
    id: _,
    open,
    onToggle,
    cells,
    expandedContent,
    colSpan,
    rowSx,
    iconCellWidth = 'auto',
    onRowClick,
    actionCell,
  } = props;

  // Handler for row click
  const handleRowClick = () => {
    // Execute custom click handler if provided
    if (onRowClick) {
      onRowClick();
    }
    // Always toggle row expansion state via the provided handler
    onToggle();
  };

  // Handler for icon click (stops event propagation)
  const handleIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Toggle row expansion state via the provided handler
    onToggle();
  };

  return (
    <>
      <TableRow
        sx={{
          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)', cursor: 'pointer' },
          ...rowSx,
        }}
        onClick={handleRowClick}
      >
        <TableCell padding="checkbox" sx={{ width: iconCellWidth }}>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={handleIconClick}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        {/* Main row content */}
        {cells.map((cell, index) => (
          <TableCell key={index}>{cell}</TableCell>
        ))}

        {/* Display action cell if provided */}
        {actionCell}
      </TableRow>

      {/* Expanded content */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={colSpan}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              {expandedContent}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default ExpandableRow;
